import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../contexts/user';
import { Alert, Box, Button, Grid, List, ListItem, ListItemText, TextField, Typography } from '@mui/material'
import { createAnswer, getExtraAnswer, updateAnswer } from '../axios/axiosFunctions';
import SaveIcon from '@mui/icons-material/Save';

const getRating = (pct) => {
    if (pct >= 90) return "STRENGTH";
    if (pct >= 60) return "DEVELOPING";
    return "OPPORTUNITY";
};

const BlockEnd360 = ({slug,onExtraQChange,reportUserId,summaryScores360 = []}) => {

    const {currentUser} = useContext(UserContext);
    const [answerText, setAnswerText] = useState("");
    const [showError, setShowError] = useState(false);
    const [defaultAnswer, setDefaultAnswer] = useState();
    const [error, setError] = useState("");
    const [alertSeverity, setAlertSeverity] = useState("error");

    // Use reportUserId (admin preview) if available, otherwise currentUser
    const effectiveUserId = reportUserId || currentUser._id;
    const isAdminPreview = !!reportUserId;

    useEffect(() => {
        const callExtraAnswer = async()=>{
            const res = await getExtraAnswer(slug,effectiveUserId)
            if(res && res.answer){
                setDefaultAnswer(res.answer);
                setAnswerText(res.answer.answer);
                onExtraQChange?.(res.answer.answer);
            }
        }
        callExtraAnswer();
    }, []);

    const handleChange = (e)=>{
        const {value} = e.target;
        setAnswerText(value);
    }

    const submitAnswer = async()=>{
        let res;

        if(defaultAnswer){
            const data = {
                answer: answerText
            }
            res = await updateAnswer(defaultAnswer._id,data);
        }else{
            const data = {
                user: currentUser._id,
                answer: answerText,
                slug
            }
            res = await createAnswer(data);
        }

        if(!res){
            setError("Error while saving please try later");
            setShowError(true);
            setTimeout(() => {
              setShowError(false);
            }, 4000);
            return;
        }
        /* onBlockNoteChange?.(blockId, answerText); */
        setDefaultAnswer(res.answer);
        onExtraQChange?.(answerText);
        setError("Saved correctly");
        setAlertSeverity("success");
        setShowError(true);
        setTimeout(() => {
            setShowError(false);
        }, 2000);

    }

    return (
    <Box display="flex" flexDirection="column" sx={{padding:"0px 48px"}}>
        <Grid container spacing={2} sx={{margin:"24px 0px 40px 0px"}}>
            <Grid size={12}>
                {summaryScores360.length > 0 ? (
                    <Box sx={{ marginBottom: "20px" }}>
                        <Typography variant="h6" color="secondary" fontWeight={600} sx={{ marginBottom: "10px" }}>Your Summary Scores:</Typography>
                        {summaryScores360.map((s, idx) => {
                            const rating = getRating(s.positive);
                            return (
                                <Typography key={idx} variant="body1" color="secondary" fontWeight={400} sx={{ marginBottom: "4px", fontSize:"1.05rem" }}>
                                    {s.name}: {s.positive.toFixed(0)}% — <strong>{rating}</strong>
                                </Typography>
                            );
                        })}
                    </Box>
                ) : (
                    <List sx={{ listStyle: "decimal", padding:0 }}>
                        <ListItem sx={{paddingTop:"2px",paddingBottom:"2px"}}>
                            <ListItemText primary={<Typography variant="body1" color="secondary" fontWeight={400} sx={{fontSize:"1.05rem"}}>1. Review your assessment results.</Typography>}/>
                        </ListItem>
                        <ListItem sx={{paddingTop:"2px",paddingBottom:"2px"}}>
                            <ListItemText primary={<Typography variant="body1" color="secondary" fontWeight={400} sx={{fontSize:"1.05rem"}}>2. Examine the scores for each area and question.</Typography>}/>
                        </ListItem>
                        <ListItem sx={{paddingTop:"2px",paddingBottom:"2px"}}>
                            <ListItemText primary={<Typography variant="body1" color="secondary" fontWeight={400} sx={{fontSize:"1.05rem"}}>3. Use the guidance below to identify next steps for your development.</Typography>}/>
                        </ListItem>
                        <ListItem sx={{paddingTop:"2px",paddingBottom:"2px"}}>
                            <ListItemText primary={<Typography variant="body1" color="secondary" fontWeight={400} sx={{fontSize:"1.05rem"}}>4. Set personal Action Commitments.</Typography>}/>
                        </ListItem>
                    </List>
                )}
                <Typography variant="h6" color="secondary" fontWeight={600} sx={{marginBottom:"8px",marginTop:"20px"}}>Strength (Scores of 90%+):</Typography>
                <Typography variant="body1" color="secondary" fontWeight={400} sx={{lineHeight:"1.7",fontSize:"1.05rem"}}>Dedicate yourself to a deeper understanding of the models and concepts in this area. Strive to become a true expert in all facets to drive consistent performance and improvement. What Action Commitments can you make to reinforce your strengths?</Typography>
                <Typography variant="h6" color="secondary" fontWeight={600} sx={{marginBottom:"8px", marginTop:"20px"}}>Developing (Scores of 60%–89%):</Typography>
                <Typography variant="body1" color="secondary" fontWeight={400} sx={{lineHeight:"1.7",fontSize:"1.05rem"}}>You have opportunities for growth in these areas of competency. What Action Commitments can you make to build skills in these areas? Further education and regular practice will help solidify these skills and develop expertise.</Typography>
                <Typography variant="h6" color="secondary" fontWeight={600} sx={{marginBottom:"8px", marginTop:"20px"}}>Opportunity (Less than 60%):</Typography>
                <Typography variant="body1" color="secondary" fontWeight={400} sx={{lineHeight:"1.7",fontSize:"1.05rem"}}>These areas present the biggest growth opportunities. Focus on understanding, reviewing, and applying the concepts. What Action Commitments can you make to mitigate the risks or consequences of these areas of development? Consider participating in individual workshops, online learning, or programs to enhance your skills.</Typography>
            </Grid>
            <Grid size={12}>
                <Typography variant="body1" color="secondary" fontWeight={600} sx={{marginBottom:"8px", marginTop:"16px",fontSize:"1.05rem"}}>Based on your feedback in all areas, identify the top priority 2 or 3 areas you will focus on for development:</Typography>
                <TextField value={answerText} onChange={handleChange} fullWidth multiline rows={5} disabled={isAdminPreview}/>
            </Grid>
            {
                showError &&
                <Grid size={12}>
                    <Alert sx={{marginTop:"20px"}} severity={alertSeverity}>
                        {error}
                    </Alert>
                </Grid>
            }
            {!isAdminPreview && (
            <Grid display={"flex"} justifyContent={"flex-end"} alignItems={"center"} gap={2} size={12}>
                <Typography variant="body2" color="secondary" sx={{fontStyle:"italic",opacity:0.8}}>Make sure to SAVE on each section.</Typography>
                <Button onClick={submitAnswer} variant="contained" sx={{marginTop:"16px",marginBottom:"48px"}} startIcon={<SaveIcon/>}>Save</Button>
            </Grid>
            )}
        </Grid>
    </Box>
    )
}

export default BlockEnd360